import React, { useContext, useState, useEffect } from "react";
import {
  Center,
  Stack,
  Text,
  Button,
  FileInput,
  Box,
  Group,
  ThemeIcon,
} from "@mantine/core";
import { DataContext } from "../../context/DataContext";
import { uploadImageToS3 } from "../../helpers";
import useStatusCheck from "../../hooks/useStatusCheck";
import CenterLoadingOverlay from "../../components/center-loading-overlay";
import EnglishLoadingWithCompletion from "../../components/english-loading-with-completion";
import Lottie from "react-lottie-player";
import IDScan from "../../assets/id-scan-loading.json";
import { IconId } from "@tabler/icons-react";
export default function Upload() {
  const { data, settings, text: textFromApi } = useContext(DataContext);
  const [frontValue, setFrontValue] = useState(null);
  const [backValue, setBackValue] = useState(null);
  const [uploadingPassport, setUploadingPassport] = useState(false);
  const [frontUploaded, setFrontUploaded] = useState(false);
  const [backUploaded, setBackUploaded] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const text = textFromApi.upload;
  const [uploading, setUploading] = useState(false);
  const [uploadComplete, setUploadComplete] = useState(false);
  const [pendingUpload, setPendingUpload] = useState(null);

  useStatusCheck(
    uploadingPassport ? frontUploaded : frontUploaded || backUploaded,
    false,
    "dcams/upload"
  );

  // Handle pending uploads
  useEffect(() => {
    if (pendingUpload) {
      const { suffix, file, setUploaded } = pendingUpload;
      processAndUpload(suffix, file, setUploaded);
      setPendingUpload(null);
    }
  }, [pendingUpload]);

  async function processAndUpload(suffix, file, setUploaded) {
    if (file) {
      console.log('📤 Starting upload for:', suffix);
      setUploading(true);
      setUploadComplete(false);
      
      // Add a small delay to ensure state updates
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Simulate checking image phase
      console.log('🔍 Checking image...');
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      console.log('📦 Converting to blob...');
      const blob = await file.arrayBuffer();
      
      // Upload to S3
      console.log('☁️ Uploading to S3...');
      await uploadImageToS3(`${settings.request_id}_dl_${suffix}.jpeg`, blob);
      
      console.log('✅ Upload complete');
      setUploaded(true);
      setUploadComplete(true);
      
      // Keep showing loading for a moment to show "Finalizing..."
      setTimeout(() => {
        console.log('🏁 Hiding upload loading');
        setUploading(false);
        setUploadComplete(false);
      }, 2000);
    }
  }

  async function handleSubmit() {
    setIsVerifying(true);
    data.settings = settings;
    data.type = "Direct_Upload";
    fetch("https://vx.dcams.app/frontend/dcams/process", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    })
      .then(function (res) {
        return res.json();
      })
      .then(function (result) {
        //setIsVerifying(false);
      })
      .catch(function () {
        alert("Something Went Wrong! Please try again.");
      });
  }

  // Check if browser language is English for loading component
  const browserLanguage = typeof navigator !== "undefined" ? navigator.language : "";
  const isEnglish = browserLanguage.toLowerCase().startsWith("en");

  console.log('🎯 Upload component state:', {
    uploading,
    isVerifying,
    frontUploaded,
    backUploaded,
    isEnglish
  });

  if (uploading) {
    console.log('🚀 Showing upload loading screen');
    const uploadMessages = [
      "Checking your image...",
      "Preparing for upload...",
      "Uploading to secure server...",
      "Processing image...",
      "Finalizing..."
    ];

    if (isEnglish) {
      return (
        <EnglishLoadingWithCompletion 
          messages={uploadMessages}
          onComplete={() => {
            console.log('Upload animation completed');
            // Animation completed, the state will be cleared by processAndUpload
          }}
        />
      );
    } else {
      return (
        <CenterLoadingOverlay
          content={
            <Text c="dimmed" align="center">
              Uploading...
            </Text>
          }
        />
      );
    }
  }

  if (isVerifying) {
    const verifyMessages = [
      "Analyzing document...",
      "Extracting information...",
      "Verifying authenticity...",
      "Cross-checking details...",
      "Finalizing verification..."
    ];

    if (isEnglish) {
      return (
        <EnglishLoadingWithCompletion 
          messages={verifyMessages}
          onComplete={() => {
            console.log('Verification animation completed');
            // Status check hook will handle the navigation
          }}
        />
      );
    } else {
      return (
        <CenterLoadingOverlay
          content={
            <>
              <Stack spacing={5} style={{ marginTop: "-40px" }}>
                <Lottie
                  animationData={IDScan}
                  loop={true}
                  play
                  style={{ height: 200 }}
                />
                <Text c="dimmed" align="center">
                  Checking your document...
                </Text>
              </Stack>
            </>
          }
        />
      );
    }
  }

  return (
    <Box
      style={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between", // This will push the button to the bottom
        height: "100%", // This should take the full height of its parent
      }}
    >
      <Box>
        <Stack>
          <Group>
            <ThemeIcon size={40} variant="light" radius="xl">
              <IconId size={35} stroke={1} />
            </ThemeIcon>
            <Text fz="xl" fw={700}>
              {text.title} {uploadingPassport ? text.passport : text.id}
            </Text>
          </Group>

          <FileInput
            name="front"
            placeholder="Please select a file"
            accept="image/png,image/jpeg"
            value={frontValue}
            onChange={(file) => {
              setFrontValue(file);
              if (file) {
                setPendingUpload({ suffix: "front", file, setUploaded: setFrontUploaded });
              }
            }}
            label={
              uploading
                ? "Uploading..."
                : uploadingPassport
                  ? text.frontUploadPassportTitle
                  : text.frontUploadTitle
            }
          />
          {!uploadingPassport && (
            <FileInput
              name="back"
              placeholder="Please select a file"
              accept="image/png,image/jpeg"
              value={backValue}
              onChange={(file) => {
                setBackValue(file);
                if (file) {
                  setPendingUpload({ suffix: "back", file, setUploaded: setBackUploaded });
                }
              }}
              label={uploading ? "Uploading..." : text.backUploadTitle}
            />
          )}
        </Stack>
      </Box>
      <Box>
        <Stack>
          <Button
            variant="subtle"
            onClick={() => setUploadingPassport(!uploadingPassport)}
          >
            {uploadingPassport
              ? "I want to upload a License or ID"
              : "I want to upload a passport"}
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={
              uploadingPassport
                ? !frontUploaded
                : !frontUploaded || !backUploaded
            }
          >
            Submit
          </Button>
        </Stack>
      </Box>
    </Box>
  );
}
